import React, {Component} from 'react';
import ReactToPrint from 'react-to-print';
import DateRangeTimeFilter from '../components/DateRangeTimeFilter';
import SalesLogReport from '../components/SalesLogReport';
import ReportHeaderState from '../components/ReportHeaderState';
import NoAccess from '../noAccess';
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {hasReportsAccess} from '../shared/authFunctions';
import FuseTheme from '../global/theme';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {commonStyles} from '../shared/styles';
import {Card, CardText} from 'material-ui/Card';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/description';

export const fuseSalesLogReportsIcon = Icon;

const styles = {
    button: {color: FuseTheme.palette.primary3Color},
    error: {fontSize: 16, color: FuseTheme.palette.errorColor},
    reportHeader: {
        position: 'relative',
        backgroundColor: FuseTheme.palette.accent2Color,
        margin: '0 0 5px 0',
        padding: '1em'
    },
    reportHeaderTitle: {margin: '0 0 1em 0'},
    bold: {fontWeight: 600}
};

/**
 * Sales Log Reports class for retrieving and displaying summary
 *
 */
export class SalesLogReports extends Component {
    constructor(props) {
        super(props);
        const system = localStorage.getItem('system');
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            errorMessage: null,
            location: {
                id: null,
                name: '',
                city: '',
                state: ''
            },
            selectedEndDate: null,
            selectedEndTime: null,
            selectedStartDate: null,
            selectedStartTime: null,
            selectedOffset: offsetDefault,
            summaryComplete: false,
            system: system
        };
    }

    /**
     * Convert Dates to visual display
     *
     * @param selectedStartDate
     * @param selectedStartTime
     * @param selectedEndDate
     * @param selectedEndTime
     * @return {string}
     */
    displaySelectedDates(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime) {
        const options = {year: 'numeric', month: 'long', day: 'numeric', hour:'2-digit', minute:'2-digit'};
        let startDate = new Date(selectedStartDate + 'T' + selectedStartTime + ':00');
        let endDate = new Date(selectedEndDate + 'T' + selectedEndTime + ':00');
        let startDateStr = startDate.toLocaleDateString('en-US', options);
        let endDateStr = endDate.toLocaleDateString('en-US', options);

        return (
            'Date Range: ' + startDateStr + ' through ' + endDateStr
        )
    }
    /**
     * Handle event props from DateRangeTimeFilter
     *
     * @param type
     * @param value
     */
    handleFilterSelect = (type, value) => {
        switch (type) {
            case 'start_date':
                // Set Start Date selection for reports summary component
                this.setState({selectedStartDate: value});
                break;
            case 'start_time':
                // Set Start Time selection for reports summary component
                this.setState({selectedStartTime: value});
                break;
            case 'end_date':
                // Set End Date selection for reports summary component
                this.setState({selectedEndDate: value});
                break;
            case 'end_time':
                // Set End Time selection for reports summary component
                this.setState({selectedEndTime: value});
                break;
            case 'offset':
                // Set Time Zone Offset selection for reports summary component
                this.setState({selectedOffset: value});
                break;
            default:
                return
        }
    };

    /**
     * Handle event props for Detailed Sales Logs table completion
     *
     * @param isComplete
     */
    handleSummaryComplete = (isComplete) => {
        this.setState({
            summaryComplete: isComplete,
        });
    };

    handleReportError = (message) => {
        this.setState({
            errorMessage: message,
            summaryComplete: true,
        });
    };

    render() {
        const {
            errorMessage,
            selectedEndDate,
            selectedEndTime,
            selectedStartDate,
            selectedStartTime,
            selectedOffset,
            summaryComplete,
            system
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Sales Log Reports</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>A Sales Log Report displays a list of Sales for a Location for selected date/time range.</p>
                    <p>Input Start Date/Time, End Date/Time and Time Zone to view Locations that have Sales. To view the Sales Click on the Location.</p>
                </span>
            );
        };

        return (
            <div className="lists-container" ref={e => (this.printReportRef = e)}>
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Report Filter (Start Date, End Date, Time Zone) */}
                        <DateRangeTimeFilter
                            onSelectStartDate={this.handleFilterSelect}
                            onSelectEndDate={this.handleFilterSelect}
                            onSelectOffset={this.handleFilterSelect}
                            onSelectEndTime={this.handleFilterSelect}
                            onSelectStartTime={this.handleFilterSelect}
                        />

                        {/* Print Header */}
                        {selectedStartDate && selectedEndDate && selectedStartTime && selectedEndTime &&
                        <ReportHeaderState
                            reportType="Sales Log Report Summary"
                        />
                        }

                        {/* Sales Log Report Content */}
                        {selectedStartDate && selectedStartTime && selectedEndDate && selectedEndTime && selectedOffset &&
                        <div>

                            {/* Report Header, Loader, and messaging */}
                            <div style={styles.reportHeader}>
                                <div className="btn-print-container report-header-button-container">
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Report"
                                                style={styles.button}
                                            />
                                        }
                                        content={() => this.printReportRef}
                                    />
                                </div>
                                <h2 style={styles.reportHeaderTitle}>Sales Log Report</h2>
                                <p>
                                    {this.displaySelectedDates(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime)}
                                </p>
                            </div>

                            {!summaryComplete &&
                            <div style={commonStyles.loaderContainer}>
                                <h4>Generating Report...</h4>
                                <CircularProgress
                                    size={24}
                                    thickness={2}
                                    style={commonStyles.loader}
                                />
                            </div>
                            }

                            {/* Summary Report */}
                            <SalesLogReport
                                startDate={selectedStartDate}
                                startTime={selectedStartTime}
                                endDate={selectedEndDate}
                                endTime={selectedEndTime}
                                offset={selectedOffset}
                                onSummaryComplete={this.handleSummaryComplete}
                                onError={this.handleReportError}
                                system={system}
                            />
                        </div>
                        }

                        {/* Error and Messaging */}
                        {errorMessage &&
                            <div style={styles.error}>
                                <p>{errorMessage}</p>
                            </div>
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/SalesLogReports.js